import { StepsProps } from './props';
import styles from './styles.module.scss';
import Step from './Step';
import { useMemo } from 'react';
import { useCustomCarousel } from '@starsoft/common/hooks';

/**
 * Steps Component
 * A component that renders a sequence of steps with carousel functionality, showing progress through a multi-step process.
 *
 * @component
 * @template V - The type of the step value (e.g. string, number)
 * @param {Object} props - Component props
 * @param {V} props.currentStep - The currently active step value
 * @param {Array<{label: string, value: V}>} props.options - Array of step options with labels and values
 * @param {boolean} props.blockFurtherSteps - Whether to prevent clicking on future steps
 * @param {string} props.i18nWorkspace - Translation namespace for i18n
 * @param {number} props.currentIndex - Current step index in the sequence
 * @param {boolean} props.isLoading - Whether steps are in loading state
 * @param {(value: V) => void} props.onStepClick - Callback when a step is clicked
 * @returns {JSX.Element} The rendered steps component with carousel functionality
 *
 * @example
 * type StepValue = 'personal' | 'address' | 'payment';
 *
 * const steps = [
 *   { label: 'steps.personal', value: 'personal' },
 *   { label: 'steps.address', value: 'address' },
 *   { label: 'steps.payment', value: 'payment' }
 * ];
 *
 * <Steps
 *   currentStep="address"
 *   options={steps}
 *   blockFurtherSteps={true}
 *   i18nWorkspace="checkout"
 *   currentIndex={1}
 *   isLoading={false}
 *   onStepClick={(value) => function('Step clicked:', value)}
 * />
 */
export function Steps<V>({
  currentStep,
  options,
  blockFurtherSteps,
  i18nWorkspace,
  currentIndex,
  isLoading,
  onStepClick,
}: StepsProps<V>) {
  // Get carousel functionality hooks
  const {
    containerRef,
    handleMouseDown,
    handleMouseLeave,
    handleMouseUp,
    handleMouseMove,
  } = useCustomCarousel();

  // Memoize steps to prevent unnecessary re-renders
  const MemorizedSteps = useMemo(
    () =>
      options?.map((opt, index) => (
        <Step
          currentStep={currentStep}
          i18nWorkspace={i18nWorkspace}
          option={opt}
          currentIndex={currentIndex}
          index={index}
          onStepClick={onStepClick}
          isLastItem={index < options.length - 1}
          key={`step-${opt.label}-${index}`}
          blockFurtherSteps={blockFurtherSteps}
          isLoading={isLoading}
        />
      )),
    [
      options,
      currentStep,
      i18nWorkspace,
      currentIndex,
      blockFurtherSteps,
      isLoading,
      onStepClick,
    ],
  );

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {MemorizedSteps}
    </div>
  );
}
